import {
  BasicChartDataSource,
  BasicCartesianChartConfig,
  AggregateCartesianChartConfig,
  AggregateCartesianChartDataConfig,
  BasicPieChartConfig,
  BasicPolarChartConfig,
} from '@app/data/interface/widget-config';

import { EChartsOption, SeriesOption } from 'echarts';
import {
  buildAllowedIdentifierSet,
  buildSourceIdToIdentifierMap,
  resolveNormalizedSourceIdentifier,
} from '@app/client/clients/wastemanagement/taiao-dashboard-layout/widget-helpers/source-filter-utils';

import { groupAndAggregateData } from './group-environmental-data.helper';
import { groupSeriesResponses, GroupBy } from '@app/widgets/helpers/time-series-aggregation.helper';
import { formatChartTooltip } from '@app/widgets/helpers/widgets';

export const convertFrogData: AggregateCartesianChartDataConfig['mapDataToSeries'] = (
  keyedBySourceId,
  idToLabelMap,
  xAxisData,
  legendLabels,
  _dataSource,
  seriesData,
  selectedSources
) => {
  if (!seriesData || !seriesData.data || !seriesData.environmentalDataSources) {
    return [];
  }

  const sourceInfoMap = new Map<string, { treatment: string; name: string }>();
  seriesData.environmentalDataSources.forEach((source: any) => {
    if (source.id) {
      sourceInfoMap.set(String(source.id), {
        treatment: source.source_metadata?.Treatment || 'Unknown',
        name: source.human_readable_name || `Source ${source.id}`,
      });
    }
  });

  const dataBySourceAndTreatment: { [key: string]: { [key: string]: { sum: number; count: number } } } = {};
  const treatmentSet = new Set<string>();

  seriesData.data.forEach((item: any) => {
    const sourceId = String(item.environmental_data_source_id);
    const sourceInfo = sourceInfoMap.get(sourceId);
    if (!sourceInfo) return;

    const treatment = sourceInfo.treatment;
    treatmentSet.add(treatment);

    if (!dataBySourceAndTreatment[sourceId]) {
      dataBySourceAndTreatment[sourceId] = {};
    }
    if (!dataBySourceAndTreatment[sourceId][treatment]) {
      dataBySourceAndTreatment[sourceId][treatment] = { sum: 0, count: 0 };
    }

    dataBySourceAndTreatment[sourceId][treatment].sum += item.sum || 0;
    dataBySourceAndTreatment[sourceId][treatment].count += Number(item.count) || 0;
  });

  const treatments = Array.from(treatmentSet).sort();
  xAxisData.length = 0;
  xAxisData.push(...treatments);

  const series: SeriesOption[] = [];

  for (const sourceId in dataBySourceAndTreatment) {
    const sourceInfo = sourceInfoMap.get(sourceId);
    if (!sourceInfo) continue;

    const seriesName = sourceInfo.name;
    if (!legendLabels.includes(seriesName)) {
      legendLabels.push(seriesName);
    }

    const dataForSeries = treatments.map((treatment) => {
      const stats = dataBySourceAndTreatment[sourceId][treatment];
      if (!stats || stats.count === 0) {
        return null;
      }
      return stats.sum; // average
    });

    series.push({
      name: seriesName,
      type: 'bar',
      stack: 'total',
      emphasis: { focus: 'series' },
      data: dataForSeries,
    } as SeriesOption);
  }

  return series;
};

/**
 * A custom tooltip formatter that displays all non-null data points for a given time.
 * It iterates through all series and finds the data point corresponding to the same timestamp.
 *
 * @param params - The ECharts tooltip parameters, which can be an object or an array of objects.
 * @returns The formatted tooltip content as a string.
 */
export const showAllNotEmptyValuesFormatter = (params: any) => {
  if (!Array.isArray(params) || params.length === 0) {
    return '';
  }

  let tooltipContent = `${params[0].axisValue}<br/>`;

  const validEntries = params
    .map((param: any) => {
      const value = Array.isArray(param.value) ? param.value[1] : param.value;
      if (value !== null && value !== undefined) {
        return {
          marker: param.marker,
          seriesName: param.seriesName,
          value: value.toFixed(2),
        };
      }
      return null;
    })
    .filter((e: any) => e !== null && e.value !== null && e.value !== undefined && e.value > 0);

  const total = validEntries.reduce((sum: number, entry: any) => sum + Number(entry.value), 0);
  tooltipContent += `<div><strong>Total: ${total.toFixed(2)}</strong></div>`;

  if (validEntries.length === 0) {
    return tooltipContent;
  }

  tooltipContent +=
    '<div style="display: grid; grid-template-columns: auto auto auto auto auto auto; grid-column-gap: 10px;">';

  validEntries.forEach((entry: any) => {
    tooltipContent += `<span>${entry.marker} ${entry.seriesName}:</span><span style="text-align: right;">${entry.value}</span>`;
  });

  tooltipContent += '</div>';

  return tooltipContent;
};
